// One form, two routes.
//
// /deals/new has no identifier because none has been allocated: the first successful save persists
// the deal, the server allocates the TRX-* id, and we replace the history entry with the edit
// route. Every save after that is an update carrying the version that was read, so a concurrent
// edit is refused rather than silently overwritten.
//
// The form carries what identifies a deal (TX-US-0031, TX-US-0039); the direction is the one thing
// it asks for. Documents dropped while creating wait in the browser and are uploaded once the save
// has given the deal its identifier; after that they are dropped below the form, onto the deal.
// A file that fails to follow its deal leaves the deal created and says which file it was.
//
// The fields are DealFieldset.jsx's. With the deal flow on (TX-US-0043), the state is not among
// them - the stage is moved on the deal's page - and they take the terms the flow adds. A deal is
// created at Proposal, with a counterparty, a product and an Incoterm, and its fields are edited
// on its page from then on (DealStageForm.jsx); this form edits a deal with the flow off.

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';

import { createDeal, getDeal, updateDeal } from '../api.js';
import useAttachmentUploads from '../deliveries/attachments/useAttachmentUploads.js';
import { STATES as UPLOAD_STATES } from '../deliveries/attachments/uploads.js';
import DealDocuments from '../documents/DealDocuments.jsx';
import { DEAL_DOCUMENTS_API } from '../documents/dealUploadApi.js';
import StagedDealFiles from '../documents/StagedDealFiles.jsx';
import { useMe } from '../MeContext.js';
import { WRITER_ROLES, holdsAny } from '../roles.js';
import { notify } from '../ui/notify.jsx';
import { useUnsavedWarning } from '../ui/useUnsavedWarning.js';
import DealFieldset from './DealFieldset.jsx';
import { emptyDraft, fromDeal, toRequestBody, validate } from './dealForm.js';
import { formMarks } from './fieldMarks.js';
import { flowOn } from './flow.js';
import SaveRefusal from './SaveRefusal.jsx';
import { useDealVocabulary } from './useDealVocabulary.js';
import { useReferenceLists } from './useReferenceLists.js';
import { useSaveRefusal } from './useSaveRefusal.js';

export default function DealForm({ mode }) {
  const { preDealId } = useParams();
  const navigate = useNavigate();
  const lists = useReferenceLists();
  const me = useMe();
  const flow = flowOn(me);
  const vocabulary = useDealVocabulary(flow);
  // Adding a product or a unit is a reference-data write, which is OPERATIONS' alone (ADR-0012).
  const canAddEntries = holdsAny(me, WRITER_ROLES);

  const [draft, setDraft] = useState(emptyDraft);
  const [version, setVersion] = useState(0);
  // The deal this form already holds. After the first save the route changes from /deals/new to
  // /deals/:id/edit while this component stays mounted, so without this the effect below would
  // refetch what we just wrote and overwrite anything typed since - losing edits made in the
  // moment between saving and the response arriving. Caught by the e2e suite, not by inspection.
  const loadedId = useRef(null);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(mode === 'edit');
  const [busy, setBusy] = useState(false);
  // Bumped to read the deal again, after a save refused because someone else saved it since.
  const [reads, setReads] = useState(0);
  const { refusal, setRefusal, fieldErrors, refuse, refuseFields } = useSaveRefusal();
  // Files dropped while creating, uploaded once the deal exists.
  const uploads = useAttachmentUploads(DEAL_DOCUMENTS_API);

  useEffect(() => {
    if (mode !== 'edit') return undefined;
    if (loadedId.current === preDealId) return undefined;

    let cancelled = false;
    getDeal(preDealId)
      .then(({ deal }) => {
        if (cancelled) return;
        loadedId.current = deal.pre_deal_id;
        setDraft(fromDeal(deal));
        setVersion(deal.version);
        setDirty(false);
        setRefusal(null);
      })
      .catch((err) => {
        if (!cancelled) {
          setRefusal({
            message: err.status === 404
              ? 'That deal does not exist, or is not yours to see.'
              : err.message,
            stale: false,
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [mode, preDealId, reads, setRefusal]);

  useUnsavedWarning(dirty);

  const reload = () => {
    loadedId.current = null;
    setReads((count) => count + 1);
  };

  const set = (name) => (value) => {
    setDraft((current) => ({ ...current, [name]: value }));
    setDirty(true);
  };

  // One save, two ways out of it. `close` is an argument rather than a second function because the
  // request, the version bookkeeping and every failure path are identical; what differs is where
  // the person ends up, and what tells them it worked.
  const save = async (close) => {
    const refused = validate(draft, { flow });
    refuseFields(refused);
    if (Object.keys(refused).length > 0) return;

    setBusy(true);

    try {
      const body = toRequestBody(draft, { flow });
      // Adopt the deal the server returns rather than letting the edit route refetch it: the
      // response is authoritative, and a refetch would race whatever is typed next. It is also
      // where an assigned deal number comes from.
      const { deal } = mode === 'create'
        ? await createDeal(body)
        : await updateDeal(preDealId, body, version);

      loadedId.current = deal.pre_deal_id;
      setDraft(fromDeal(deal));
      setVersion(deal.version);
      setDirty(false);

      // The deal exists now, so the files waiting for it can follow. One that fails leaves the
      // deal created: with the flow off the form stays, on the deal's edit page, and says which
      // file it was; with it on the deal's page says so, and takes the file again.
      const { failed } = await uploads.drain(deal.pre_deal_id);

      // Every successful save says so, in one place. The banner is mounted in the shell, so it
      // outlives the form when the save also navigates - which is why it can be the single answer
      // for both controls rather than one mechanism for staying and another for leaving.
      //
      // Only a save that leaves offers a way back in. Staying on the form is already being there.
      const number = deal.fields?.deal_number ?? deal.pre_deal_id;
      const unattached = flow && failed
        ? ' Some files were not attached: drop them on the deal’s documents.'
        : '';
      notify(`Deal ${number} ${mode === 'create' ? 'created' : 'saved'}.${unattached}`, close ? {
        action: { label: 'Open', onClick: () => navigate(`/deals/${deal.pre_deal_id}`) },
      } : undefined);

      if (close && !failed) {
        navigate('/deals');
      } else if (flow) {
        // With the flow on, a deal's fields are edited on its page, beside what its next move
        // waits on, and its documents are dropped there.
        navigate(`/deals/${deal.pre_deal_id}`, { replace: true });
      } else if (mode === 'create' || failed) {
        // replace, not push: the back button should not return to a form for a deal that now
        // exists and has an identifier.
        navigate(`/deals/${deal.pre_deal_id}/edit`, { replace: true });
      }
    } catch (err) {
      refuse(err, draft, vocabulary);
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (event) => {
    event.preventDefault();
    save(false);
  };

  if (loading) return <p className="muted">Loading…</p>;

  const notAttached = uploads.items.filter((item) => item.state === UPLOAD_STATES.failed);

  return (
    <>
      <form className="deal-form" onSubmit={onSubmit}>
        <header className="register-header">
          <h2>{mode === 'create' ? 'New deal' : 'Edit deal'}</h2>
          {mode === 'edit' && <Link to={`/deals/${preDealId}`}>View deal</Link>}
        </header>

        <SaveRefusal refusal={refusal} onReload={mode === 'edit' ? reload : null} />
        {notAttached.length > 0 && (
          <p className="error">
            The deal is saved, but {notAttached.map((item) => item.file.name).join(', ')}
            {notAttached.length === 1 ? ' was' : ' were'} not attached. Drop{' '}
            {notAttached.length === 1 ? 'it' : 'them'} on the deal’s documents below.
          </p>
        )}

        <DealFieldset draft={draft} set={set} flow={flow} creating={mode === 'create'}
                      lists={lists} vocabulary={vocabulary} fieldErrors={fieldErrors}
                      canAddEntries={canAddEntries}
                      marks={formMarks({ flow })} />

        {mode === 'create' && <StagedDealFiles uploads={uploads} disabled={busy} />}

        <div className="form-actions">
          <button type="submit" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          {/* Not the submit button, deliberately. Enter inside a text field submits the form, and
              someone still typing would be thrown back to the register by it. Enter keeps meaning
              save and stay. */}
          <button type="button" onClick={() => save(true)} disabled={busy}>
            Save and close
          </button>
          <Link to="/deals">Back to the register</Link>
        </div>
      </form>

      {mode === 'edit' && <DealDocuments preDealId={preDealId} />}
    </>
  );
}
